import { create } from 'zustand';
import { GlobalState, initialGlobalState } from './globalState';
import { DictionaryState, initialDictionaryState } from './dictionary';
import { LessonState, initialLessonState } from './lesson';
import { ProfessionState, initialProfessionState } from './professionState';

type StoreState = {
  global: GlobalState;
  dictionary: DictionaryState;
  lesson: LessonState;
  profession: ProfessionState;
};

type StoreActions = {
  setSettings: (settings?: GlobalState['settings']) => void;
  setUserInfo: (
    userInfo?: GlobalState['userInfo'],
    userLoading?: GlobalState['userLoading']
  ) => void;
  setUserLoading: (userLoading: boolean) => void;
  setDictionary: (dictionary: DictionaryState) => void;
  setSpecialSubjectType: (
    specialSubjectType: DictionaryState['specialSubjectType']
  ) => void;
  setProfession: (
    currentProfession: ProfessionState['currentProfession']
  ) => void;
};

export type AppStore = StoreState & StoreActions;

const initialState: StoreState = {
  global: initialGlobalState,
  dictionary: initialDictionaryState,
  lesson: initialLessonState,
  profession: initialProfessionState,
};

export const useAppStore = create<AppStore>()((set) => ({
  ...initialState,
  setSettings: (settings) =>
    set((state) => ({
      global: {
        ...state.global,
        settings: settings ?? state.global.settings,
      },
    })),
  setUserInfo: (userInfo, userLoading) =>
    set((state) => ({
      global: {
        ...state.global,
        userInfo:
          userInfo ?? state.global.userInfo ?? initialGlobalState.userInfo,
        userLoading:
          typeof userLoading === 'boolean'
            ? userLoading
            : state.global.userLoading,
      },
    })),
  setUserLoading: (userLoading) =>
    set((state) => ({
      global: {
        ...state.global,
        userLoading,
      },
    })),
  setDictionary: (dictionary) =>
    set((state) => ({
      dictionary: {
        ...state.dictionary,
        ...(dictionary ?? initialDictionaryState),
      },
    })),
  setSpecialSubjectType: (specialSubjectType) =>
    set((state) => ({
      dictionary: {
        ...state.dictionary,
        specialSubjectType: specialSubjectType ?? [],
      },
    })),
  setProfession: (currentProfession) =>
    set(() => ({
      profession: {
        currentProfession: currentProfession ?? null,
      },
    })),
}));

// Store API（用于非组件场景）
export const appStoreApi = {
  getState: () => useAppStore.getState(),
  setState: useAppStore.setState,
  subscribe: useAppStore.subscribe,
};

